"use client";
import { Situation } from "@/generated/fastapi";
import { Badge, Box, Heading, HStack, Text } from "@chakra-ui/react";
import { memo } from "react";

interface SituationHeaderProps {
  situation: Situation;
  // Interactive props for selectable/clickable behavior
  onClick?: () => void;
  isDisabled?: boolean;
  isCurrent?: boolean;
  // Layout customization
  showDates?: boolean;
  compact?: boolean;
}

function SituationHeader({
  situation,
  onClick,
  isDisabled = false,
  isCurrent = false,
  showDates = true,
  compact = false,
}: SituationHeaderProps) {
  if (!situation) {
    return null;
  }

  const isInteractive = !!onClick;
  const isClickable = isInteractive && !isDisabled && !isCurrent;
  const hasStatus = isCurrent || isDisabled;

  return (
    <Box
      onClick={isClickable ? onClick : undefined}
      p={compact ? "2" : "0"}
      borderWidth={isInteractive ? "1px" : undefined}
      borderColor={
        isCurrent
          ? "moveon.primary"
          : isDisabled
            ? "moveon.warningBorder"
            : isInteractive
              ? "moveon.surface.border"
              : undefined
      }
      borderRadius={isInteractive ? "md" : undefined}
      bg={
        isCurrent
          ? "moveon.primaryLight"
          : isDisabled
            ? "moveon.warningLight"
            : isInteractive
              ? "moveon.surface.bg"
              : undefined
      }
      cursor={
        isClickable
          ? "pointer"
          : isDisabled
            ? "not-allowed"
            : undefined
      }
      opacity={isDisabled && !isCurrent ? 0.72 : 1}
      transition={isInteractive ? "all 0.2s" : undefined}
      _hover={
        isClickable
          ? {
              bg: "moveon.surface.elevatedBg",
              borderColor: "moveon.primary",
              transform: "translateY(-1px)",
              shadow: "sm",
            }
          : undefined
      }
      _active={
        isClickable
          ? {
              transform: "translateY(0)",
              shadow: "none",
            }
          : undefined
      }
    >
      {/* Status indicators */}
      {hasStatus && (
        <HStack gap="2" mb="1" align="center" flexWrap="wrap">
          {isCurrent && (
            <Badge
              size="sm"
              bg="moveon.primaryLight"
              color="moveon.primary"
              borderWidth="1"
              borderColor="moveon.primary"
            >
              Current situation
            </Badge>
          )}
          {isDisabled && (
            <Badge
              size="sm"
              bg="moveon.warningLight"
              color="moveon.warning"
              borderWidth="1"
              borderColor="moveon.warningBorder"
            >
              Category unavailable
            </Badge>
          )}
        </HStack>
      )}
      {isDisabled ? (
        <Text fontSize="xs" color="moveon.warning" mb="1">
          Edit this situation to use this perspective.
        </Text>
      ) : null}

      {/* Position */}
      <Heading
        // size={compact ? "md" : "md"}
        size="md"
        lineHeight="short"
        whiteSpace="normal"
        overflowWrap="anywhere"
        wordBreak="break-word"
      >
        {situation.position}
      </Heading>

      {/* Context */}
      <Heading
        color={compact ? "moveon.text.muted" : undefined}
        size="sm"
        lineHeight="short"
        whiteSpace="normal"
        overflowWrap="anywhere"
        wordBreak="break-word"
      >
        {situation.context}
      </Heading>

      {/* Dates */}
      {showDates && (
        <Text fontSize="xs" display="block" mt={compact ? "1" : "0"}>
          {situation.has_started ? `${situation.date_from}` : "Not started yet"}
          {situation.has_started && !situation.is_current
            ? ` - ${situation.date_to}`
            : situation.is_current
              ? " - Ongoing"
              : ""}
        </Text>
      )}
    </Box>
  );
}

// ✅ Prevents re-renders when props are the same
export default memo(SituationHeader);
